import * as React from 'react'
import { connectHits } from 'react-instantsearch/connectors'

interface MinistryHit {
  objectID: string
  title?: string
  abstractTitle?: string
  category?: string
  tags?: string[]
  backgroundColor?: string
  foregroundColor?: string
  logoUrl?: string
  thumbnailUrl?: string
  slug?: string
  portalSlug?: string
}

const Hit = ({ hit }: { hit: MinistryHit }) => {
  const color = hit.backgroundColor || '#003a5d'

  return (
    <a
      href={hit.portalSlug || hit.slug || '#'}
      className="ministry-card"
      style={{ ['--ministry-color' as string]: color }}
    >
      <div className="ministry-card__top">
        {hit.tags?.[0] && (
          <span className="ministry-card__tag">{hit.tags[0]}</span>
        )}
        {(hit.logoUrl || hit.thumbnailUrl) && (
          <div className="ministry-card__brand">
            <div className="ministry-card__logo">
              <img src={hit.logoUrl || hit.thumbnailUrl} alt={hit.title || ''} />
            </div>
          </div>
        )}
        {hit.abstractTitle && (
          <p className="ministry-card__description">{hit.abstractTitle}</p>
        )}
      </div>
      <span className="ministry-card__cta">
        <span className="ministry-card__cta-text">View Resources</span>
        <span className="material-symbols-outlined ministry-card__cta-arrow">arrow_right_alt</span>
      </span>
    </a>
  )
}

// Group hits by hit.category. Category values and section order both come from
// Contentful — Algolia returns hits sorted by `customRanking ['asc(sequence)', 'asc(title)']`
// (set in app/models/ministry.rb), so the first hit per category is the lowest-sequence
// ministry. Iterating in that order assigns each ministry to its bucket on first
// appearance, and Map preserves insertion order — so section order falls out of the
// sequence values set in the CMS.
function groupByCategory(hits: MinistryHit[]): Array<[string, MinistryHit[]]> {
  const groups = new Map<string, MinistryHit[]>()
  hits.forEach((hit) => {
    const key = hit.category || 'Other'
    if (!groups.has(key)) groups.set(key, [])
    groups.get(key)!.push(hit)
  })
  return Array.from(groups.entries())
}

const DefaultHit = ({ hit }: { hit: MinistryHit }) =>
  <div className="item">
    <a href={hit.slug || ''}
       className="model-card"
       style={{ backgroundColor: hit.backgroundColor }}
    >
      <span
        className="model-card__category"
        style={{ color: hit.foregroundColor }}
      >
        {hit.abstractTitle}
      </span>
      <div className="model-card__logo">
        <img alt='' src={hit.logoUrl || hit.thumbnailUrl} />
      </div>
    </a>
  </div>

const Hits: React.ComponentType<{ theme?: string }> = connectHits(({ hits, theme }: { hits: MinistryHit[], theme?: string }) => {
  if (theme === 'portal') {
    const groups = groupByCategory(hits)

    return (
      <div className="ministry-sections">
        {groups.map(([category, categoryHits]) => (
          <section className="ministry-section" key={category}>
            <h2 className="ministry-section__title">{category}</h2>
            <div className="ministry-grid">
              {categoryHits.map((hit) => <Hit hit={hit} key={hit.objectID} />)}
            </div>
          </section>
        ))}
      </div>
    )
  }

  return (
    <div className="row item-list">
      {hits.map((hit) =>
        <DefaultHit hit={hit} key={hit.objectID} />,
      )}
    </div>
  )
})

export default Hits
